// ClientPicker ortak çekirdeği (2026-10-07) — tekli (`ClientPicker`) ve çoklu
// (`ClientMultiPicker`) seçicinin paylaştığı arama kancası + listbox çizimi + stil parçaları.
// Saf mantık `lib/client-picker.ts`'te (satır dizisi, klavye reducer'ı, vurgulama, son
// kullanılanlar); burada yalnız React tarafı: debounce'lu sunucu araması (250 ms, yarış
// korumalı), yerel mod filtresi ve `role="listbox"` görünümü.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { searchClients, type ClientSearchResult } from "../../api/clients";
import {
  CLIENT_PICKER_COPY,
  fillCopy,
  filterLocalItems,
  matchSegments,
  type ClientPickerItem,
  type PickerRow,
} from "../../lib/client-picker";

export const PICKER_DEBOUNCE_MS = 250;
export const PICKER_LOCAL_LIMIT = 30;

export type ClientPickerSize = "sm" | "md";

export type ClientPickerFetch = (query: string) => Promise<ClientSearchResult>;

export interface ClientPickerSourceProps {
  /** `server` (varsayılan): `GET /clients?search=`; `local`: `options` yerel filtrelenir. */
  mode?: "server" | "local" | undefined;
  /** `mode="local"` listesi. */
  options?: readonly ClientPickerItem[] | undefined;
  /** Sunucu modunda özel kaynak (örn. KPI özeti — ikincil satır set sayısı). */
  fetchOptions?: ClientPickerFetch | undefined;
  /** Ek "Tüm müşteriler →" bağlantısı (varsayılan `/clients`); `null` gizler. */
  allLink?: string | null | undefined;
}

interface SearchState {
  items: ClientPickerItem[];
  total: number;
  loading: boolean;
  error: boolean;
}

/**
 * Arama kancası. `open` false iken istek atılmaz; sorgu değişince 250 ms bekler; geç gelen
 * yanıt (yarış) atılır. Yerel modda senkron filtre.
 */
export function useClientPickerSearch(
  src: ClientPickerSourceProps,
  query: string,
  open: boolean,
): SearchState & { retry: () => void } {
  const mode = src.mode ?? "server";
  const [state, setState] = useState<SearchState>({
    items: [],
    total: 0,
    loading: false,
    error: false,
  });
  const seq = useRef(0);
  const [tick, setTick] = useState(0);
  const fetcher = src.fetchOptions ?? searchClients;

  useEffect(() => {
    if (!open) return;
    if (mode === "local") {
      const r = filterLocalItems(src.options ?? [], query, PICKER_LOCAL_LIMIT);
      setState({ items: r.items, total: r.total, loading: false, error: false });
      return;
    }
    const my = (seq.current += 1);
    setState((s) => ({ ...s, loading: true, error: false }));
    const t = window.setTimeout(() => {
      fetcher(query)
        .then((res) => {
          if (my !== seq.current) return;
          setState({ items: res.items, total: res.total, loading: false, error: false });
        })
        .catch(() => {
          if (my !== seq.current) return;
          setState({ items: [], total: 0, loading: false, error: true });
        });
    }, PICKER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // fetcher kimliği çağıranın render'ına bağlı olabilir; sorgu/açılış/tick yeterli tetik.
  }, [query, open, mode, src.options, tick]);

  const retry = useCallback(() => setTick((v) => v + 1), []);
  return { ...state, retry };
}

// ─── Stil parçaları ──────────────────────────────────────────────────────────

export function fieldStyle(args: {
  size: ClientPickerSize;
  inline: boolean;
  focused: boolean;
  touch: boolean;
  disabled: boolean;
}): React.CSSProperties {
  const sm = args.size === "sm";
  return {
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 4,
    minHeight: args.touch ? 44 : sm ? 34 : 40,
    padding: sm ? "2px 6px" : "4px 10px",
    borderRadius: args.inline ? 6 : 8,
    border: `1px solid ${args.focused ? "var(--color-primary)" : "var(--color-border)"}`,
    background: args.disabled ? "var(--color-bg)" : "var(--color-surface)",
    boxShadow: args.focused
      ? "0 0 0 2px color-mix(in srgb, var(--color-primary) 18%, white)"
      : "none",
    cursor: args.disabled ? "not-allowed" : "text",
    opacity: args.disabled ? 0.7 : 1,
    minWidth: args.inline ? 180 : 0,
    width: args.inline ? undefined : "100%",
    fontFamily: "var(--font-sans)",
  };
}

export const inputStyle = (size: ClientPickerSize): React.CSSProperties => ({
  flex: "1 1 80px",
  minWidth: 60,
  border: 0,
  outline: 0,
  background: "transparent",
  padding: size === "sm" ? "3px 4px" : "4px 2px",
  fontSize: size === "sm" ? "0.85rem" : "0.9rem",
  color: "var(--color-text)",
  fontFamily: "inherit",
});

export const chipStyle = (size: ClientPickerSize): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  maxWidth: "100%",
  padding: size === "sm" ? "1px 4px 1px 8px" : "2px 6px 2px 10px",
  borderRadius: 999,
  background: "color-mix(in srgb, var(--color-primary) 12%, white)",
  color: "var(--color-primary)",
  fontSize: size === "sm" ? "0.8rem" : "0.85rem",
  fontWeight: 600,
  lineHeight: 1.4,
});

export const chipTextStyle: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 260,
};

export function ChipRemoveButton({
  label,
  onClick,
  touch,
}: {
  label: string;
  onClick: () => void;
  touch: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: touch ? 28 : 18,
        height: touch ? 28 : 18,
        borderRadius: 999,
        border: 0,
        background: "transparent",
        color: "inherit",
        cursor: "pointer",
        fontSize: "0.85rem",
        lineHeight: 1,
      }}
    >
      ✕
    </button>
  );
}

// ─── Listbox ─────────────────────────────────────────────────────────────────

export interface ClientPickerListboxProps {
  id: string;
  label: string;
  /**
   * Alanın kökü — liste `document.body`'ye portal ile, `position: fixed` olarak alanın altına
   * (yer yoksa üstüne) çizilir. Sebep: rapor aktarımı tablosu `overflow-x: auto` — akış içi
   * `position: absolute` liste tablo kabında KIRPILIYORDU (ilk satırdan sonrası görünmüyor).
   */
  anchorRef: RefObject<HTMLElement>;
  rows: readonly PickerRow[];
  /** `rows` içindeki etkin satır indeksi (seçenek/ek). */
  activeRow: number;
  selectedIds: ReadonlySet<string>;
  query: string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onPick: (item: ClientPickerItem) => void;
  onExtra?: ((key: string) => void) | undefined;
  renderOption?: ((item: ClientPickerItem, defaultNode: ReactNode) => ReactNode) | undefined;
  allLink: string | null;
  touch: boolean;
  size: ClientPickerSize;
  multi?: boolean | undefined;
}

export function optionDomId(listId: string, rowIndex: number): string {
  return `${listId}-opt-${rowIndex}`;
}

/** Dışarı tıklama denetimi: hedef, portal'daki liste kabının içinde mi? */
export function isInsidePickerList(target: EventTarget | null, listId: string): boolean {
  const el = target instanceof Element ? target : null;
  return el?.closest(`[data-client-picker-list="${listId}"]`) !== null && el !== null;
}

const LIST_MAX_HEIGHT = 340;
const LIST_GAP = 4;

interface ListPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/** Alanın viewport konumundan liste konumu; altta ≥200 px yoksa ve üstte daha çok yer varsa üste. */
function computeListPos(anchor: HTMLElement): ListPos {
  const r = anchor.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - LIST_GAP;
  const above = r.top - LIST_GAP;
  const width = Math.min(Math.max(r.width, 280), Math.min(520, window.innerWidth * 0.9));
  const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
  if (below < 200 && above > below) {
    return {
      left,
      width,
      bottom: window.innerHeight - r.top + LIST_GAP,
      maxHeight: Math.min(LIST_MAX_HEIGHT, above),
    };
  }
  return {
    left,
    width,
    top: r.bottom + LIST_GAP,
    maxHeight: Math.min(LIST_MAX_HEIGHT, Math.max(120, below)),
  };
}

export function ClientPickerListbox(p: ClientPickerListboxProps): JSX.Element | null {
  const hasOptions = p.rows.some((r) => r.kind === "option" || r.kind === "extra");
  const showEmpty = !p.loading && !p.error && !hasOptions;
  const rowPad = p.size === "sm" ? "6px 10px" : "8px 12px";
  const [pos, setPos] = useState<ListPos | null>(null);
  const anchorRef = p.anchorRef;

  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const update = (): void => setPos(computeListPos(el));
    update();
    window.addEventListener("resize", update);
    // Kaydırma herhangi bir kapta olabilir (tablo, modal gövdesi) — capture ile dinlenir.
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, p.rows.length]);

  if (!pos || typeof document === "undefined") return null;
  return createPortal(
    <div
      data-client-picker-list={p.id}
      style={{
        position: "fixed",
        left: pos.left,
        width: pos.width,
        ...(pos.top !== undefined ? { top: pos.top } : {}),
        ...(pos.bottom !== undefined ? { bottom: pos.bottom } : {}),
        zIndex: 110,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        boxShadow: "var(--shadow-md)",
        maxHeight: pos.maxHeight,
        overflowY: "auto",
        fontFamily: "var(--font-sans)",
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <ul
        id={p.id}
        role="listbox"
        aria-label={p.label}
        aria-multiselectable={p.multi ? true : undefined}
        style={{ listStyle: "none", margin: 0, padding: "4px 0" }}
      >
        {p.rows.map((row, i) => {
          if (row.kind === "header") {
            return (
              <li
                key={`h-${i}`}
                role="presentation"
                style={{
                  padding: "6px 12px 2px",
                  fontSize: "0.68rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "var(--color-text-3)",
                  fontWeight: 600,
                }}
              >
                {row.label}
              </li>
            );
          }
          if (row.kind === "more") {
            return (
              <li
                key={`m-${i}`}
                role="presentation"
                style={{ padding: rowPad, fontSize: "0.78rem", color: "var(--color-text-3)" }}
              >
                {fillCopy(CLIENT_PICKER_COPY.more, { n: row.count })}
              </li>
            );
          }
          const active = i === p.activeRow;
          if (row.kind === "extra") {
            return (
              <li
                key={`x-${row.key}`}
                id={optionDomId(p.id, i)}
                role="option"
                aria-selected={active}
                onClick={() => p.onExtra?.(row.key)}
                style={{
                  padding: rowPad,
                  minHeight: p.touch ? 44 : undefined,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "var(--color-primary)",
                  background: active
                    ? "color-mix(in srgb, var(--color-primary) 10%, white)"
                    : "transparent",
                  borderTop: "1px solid var(--color-border)",
                }}
              >
                {row.label}
              </li>
            );
          }
          const selected = p.selectedIds.has(row.item.id);
          const defaultNode = (
            <span style={{ display: "block", minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontSize: "0.88rem",
                  color: "var(--color-text)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {matchSegments(row.item.name, p.query).map((seg, k) =>
                  seg.hit ? (
                    <b key={k} style={{ fontWeight: 700 }}>
                      {seg.text}
                    </b>
                  ) : (
                    <span key={k}>{seg.text}</span>
                  ),
                )}
              </span>
              {row.item.secondary ? (
                <span
                  style={{ display: "block", fontSize: "0.75rem", color: "var(--color-text-3)" }}
                >
                  {row.item.secondary}
                </span>
              ) : null}
            </span>
          );
          return (
            <li
              key={`${row.section}-${row.item.id}`}
              id={optionDomId(p.id, i)}
              role="option"
              aria-selected={p.multi ? selected : active}
              onClick={() => p.onPick(row.item)}
              style={{
                padding: rowPad,
                minHeight: p.touch ? 44 : undefined,
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                background: active
                  ? "color-mix(in srgb, var(--color-primary) 10%, white)"
                  : selected
                    ? "color-mix(in srgb, var(--color-primary) 5%, white)"
                    : "transparent",
              }}
            >
              {p.multi ? (
                <span
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    border: `1px solid ${selected ? "var(--color-primary)" : "var(--color-border)"}`,
                    background: selected ? "var(--color-primary)" : "var(--color-surface)",
                    color: "var(--color-surface)",
                    fontSize: "0.7rem",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {selected ? "✓" : ""}
                </span>
              ) : null}
              {p.renderOption ? p.renderOption(row.item, defaultNode) : defaultNode}
            </li>
          );
        })}
      </ul>
      {p.loading ? (
        <div
          role="status"
          style={{ padding: rowPad, fontSize: "0.78rem", color: "var(--color-text-3)" }}
        >
          {CLIENT_PICKER_COPY.loading}
        </div>
      ) : null}
      {p.error ? (
        <div
          role="alert"
          style={{
            padding: rowPad,
            fontSize: "0.8rem",
            color: "var(--color-warning-text)",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          {CLIENT_PICKER_COPY.error}
          <button
            type="button"
            onClick={p.onRetry}
            style={{
              border: "1px solid var(--color-warning-border)",
              background: "var(--color-surface)",
              color: "var(--color-warning-text)",
              borderRadius: 6,
              padding: "2px 8px",
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          >
            {CLIENT_PICKER_COPY.retry}
          </button>
        </div>
      ) : null}
      {showEmpty ? (
        <div style={{ padding: rowPad, fontSize: "0.8rem", color: "var(--color-text-3)" }}>
          {CLIENT_PICKER_COPY.empty}
        </div>
      ) : null}
      {p.allLink ? (
        <div
          style={{
            borderTop: "1px solid var(--color-border)",
            padding: "6px 12px",
            fontSize: "0.78rem",
          }}
        >
          <Link to={p.allLink} style={{ color: "var(--color-primary)", fontWeight: 600 }}>
            {CLIENT_PICKER_COPY.allLink}
          </Link>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
